import { Box } from '@chakra-ui/react'
import { pipe } from '@helo/utils'
import type { ReactNode } from 'react'

import { Format } from '../../Format'
import { Options } from '../../Options'
import { RenderPipeline } from '../../RenderPipeline'
import { Tooltip as TooltipFeature } from '../../Tooltip'
import type { ChartAxisDateFormatOptions } from '../ChartAxis/ChartAxis.parts'
import { ChartAxisUtils } from '../ChartAxis/ChartAxis.parts'
import type { ChartDataPoint } from '../ChartData'
import { ChartDataParts } from '../ChartData/ChartData.parts'
import { ChartInteractionUtils } from '../ChartInteraction'
import { ChartLineConstants } from '../ChartLine/ChartLine.constants'
import type { ChartPieSlice, ResolvedChartPieSlice } from '../ChartPie'
import type { ChartPlotLayout } from '../ChartWrapper/ChartWrapper'
import type { ChartTooltipSlice } from './ChartTooltip'
import { ChartTooltipConstants } from './ChartTooltip.constants'
import { chartTooltipOptions } from './ChartTooltip.options'

export type ChartTooltipMarkerKind = 'line' | 'bar' | 'pie'

export type ChartTooltipPayloadValue = {
  seriesKey: string
  label: string
  value: number
  formattedValue: string
  color: string
  fillColor: string
  pointRadius: number
  lineWidth: number
  visualization: ChartTooltipMarkerKind | string
}

export type ChartTooltipPayload = {
  x: string
  values: ChartTooltipPayloadValue[]
}

const clampTooltipPosition = (
  left: number,
  top: number,
  width: number,
  height: number,
) => {
  const pad = ChartTooltipConstants.viewportPadPx
  const maxL = Math.max(pad, window.innerWidth - width - pad)
  const maxT = Math.max(pad, window.innerHeight - height - pad)
  return {
    left: Math.min(Math.max(pad, left), maxL),
    top: Math.min(Math.max(pad, top), maxT),
  }
}

const formatValue = (value: number): string => Format.number({})(value)

/** Axis tick format, with stacked newlines collapsed for a one-line header. */
const formatCategoryLabel = (
  raw: string,
  dateFormat: ChartAxisDateFormatOptions = {},
): string =>
  ChartAxisUtils.formatTickLabel(raw, dateFormat)
    .replace(/\\n/g, '\n')
    .replace(/\n+/g, ' ')
    .trim()

const resolveSeriesLabel = (seriesItem: TooltipSeriesInput): string =>
  seriesItem.label ?? seriesItem.seriesKey

type TooltipSeriesInput = {
  seriesKey: string
  label?: string
  visualization: string
  color?: string
  fillColor?: string
  pointRadius?: number
  lineWidth?: number
  points: ChartDataPoint[]
  /** When set (e.g. stacked100 bars), tooltip values use these instead of plot points. */
  rawPoints?: ChartDataPoint[]
  formatDisplayValue?: (value: number) => string
}

type BuildPayloadInput = {
  x: string
  series: TooltipSeriesInput[]
  formatValue: (value: number) => string
}

const buildPayload = ({
  x,
  series,
  formatValue: format,
}: BuildPayloadInput): ChartTooltipPayload => {
  const lineSeries = ChartInteractionUtils.hoverSeries(series)
  return {
    x,
    values: lineSeries.flatMap(seriesItem => {
      const valuePoints = seriesItem.rawPoints ?? seriesItem.points
      const point = valuePoints.find(
        (candidate: ChartDataPoint) => candidate.x === x,
      )
      if (point?.y == null) return []
      const formattedValue =
        seriesItem.formatDisplayValue?.(point.y) ?? format(point.y)
      return [
        {
          seriesKey: seriesItem.seriesKey,
          label: resolveSeriesLabel(seriesItem),
          value: point.y,
          formattedValue,
          color: seriesItem.color ?? '',
          fillColor: seriesItem.fillColor ?? seriesItem.color ?? '',
          pointRadius:
            seriesItem.pointRadius ?? ChartLineConstants.defaultPointRadius,
          lineWidth:
            seriesItem.lineWidth ?? ChartLineConstants.defaultLineWidth,
          visualization: seriesItem.visualization,
        },
      ]
    }),
  }
}

const buildPiePayload = (
  slice: ResolvedChartPieSlice,
  pie: ChartPieSlice,
  format: (value: number) => string,
): ChartTooltipPayload => ({
  x: slice.label,
  values: [
    {
      seriesKey: slice.key,
      label: pie.measureLabel ?? 'Value',
      value: slice.value,
      formattedValue: pie.formatDisplayValue(slice.value),
      color: slice.color,
      fillColor: slice.color,
      pointRadius: slice.pointRadius,
      lineWidth: slice.lineWidth,
      visualization: 'pie',
    },
    {
      seriesKey: `${slice.key}-pct`,
      label: 'Percent of Total',
      value: slice.percent,
      formattedValue: `${Math.round(slice.percent)}%`,
      color: slice.color,
      fillColor: slice.color,
      pointRadius: slice.pointRadius,
      lineWidth: slice.lineWidth,
      visualization: 'pie',
    },
    ...(slice.isOther && slice.otherItems != null
      ? slice.otherItems.flatMap((item, index) => [
          {
            seriesKey: `${slice.key}-other-${index}`,
            label: item.name,
            value: item.value,
            formattedValue: format(item.value),
            color: slice.color,
            fillColor: slice.color,
            pointRadius: slice.pointRadius,
            lineWidth: slice.lineWidth,
            visualization: 'pie' as const,
          },
        ])
      : []),
  ],
})

interface SeriesMarkerProps {
  visualization: ChartTooltipMarkerKind | string
  color: string
  fillColor: string
  pointRadius?: number
}

const SeriesMarker = ({
  visualization,
  color,
  fillColor,
  pointRadius = ChartLineConstants.defaultPointRadius,
}: SeriesMarkerProps): ReactNode => {
  if (visualization === 'bar') {
    return (
      <Box
        as="span"
        role="img"
        aria-hidden
        flexShrink={0}
        w={`${ChartTooltipConstants.barMarkerWidthPx}px`}
        h={`${ChartTooltipConstants.barMarkerHeightPx}px`}
        borderRadius={`${ChartTooltipConstants.barMarkerRadiusPx}px`}
        bg={color}
      />
    )
  }

  if (visualization === 'pie') {
    return (
      <Box
        as="span"
        role="img"
        aria-hidden
        flexShrink={0}
        w="14px"
        h="14px"
        borderRadius="full"
        bg={color}
        border="2px solid white"
        boxShadow="0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)"
      />
    )
  }

  const strokeWidth = ChartLineConstants.pointStrokeWidth
  const outerRadius = pointRadius + strokeWidth / 2
  const size = outerRadius * 2 + 2
  const cx = size / 2
  const cy = size / 2

  return (
    <Box
      as="span"
      display="inline-flex"
      role="img"
      aria-hidden
      flexShrink={0}
      alignItems="center"
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ display: 'block', flexShrink: 0 }}
      >
        <circle
          cx={cx}
          cy={cy}
          r={pointRadius}
          fill={fillColor}
          stroke={color}
          strokeWidth={strokeWidth}
        />
      </svg>
    </Box>
  )
}

interface TooltipKvRowProps {
  label: string
  value: string
}

const TooltipKvRow = ({ label, value }: TooltipKvRowProps): ReactNode => (
  <Box
    display="flex"
    flexDirection="row"
    flexWrap="nowrap"
    alignItems="baseline"
    minW={0}
    sx={{ whiteSpace: 'nowrap' }}
  >
    <Box
      as="span"
      fontSize="12px"
      fontWeight="400"
      color="presets.neutral.0"
      flexShrink={1}
      overflow="hidden"
      textOverflow="ellipsis"
    >
      {label}:{'\u00A0'}
    </Box>
    <Box
      as="span"
      fontSize="12px"
      fontWeight="700"
      color="presets.neutral.0"
      flexShrink={0}
    >
      {value}
    </Box>
  </Box>
)

/** Category-axis pixel for a domain value (band center or point). */
const categoryGuidePosition = (
  scales: NonNullable<ChartPlotLayout['scales']>,
  value: string,
): number | undefined => {
  const isHorizontal = scales.orientation === 'horizontal'
  const categoryScale = isHorizontal ? scales.yScale : scales.xScale
  const bandwidth =
    'bandwidth' in categoryScale &&
    typeof categoryScale.bandwidth === 'function'
      ? categoryScale.bandwidth()
      : 0
  const start = categoryScale(value)
  if (start == null) return undefined
  return start + bandwidth / 2
}

/** Write tooltip formatters and payload builders. */
const setTooltip = () =>
  RenderPipeline.updateOutput(
    (
      _output: { tooltip?: ChartTooltipSlice },
      ctx: { input: { _tooltip?: { disabled?: boolean }; disabled?: boolean } },
    ) => {
      const tooltip = ctx.input._tooltip ?? ctx.input
      const isActive = TooltipFeature.isActive(tooltip)
      return {
        tooltip: {
          disabled: tooltip.disabled === true,
          isActive,
          formatValue,
          buildPayload: (x, series) =>
            buildPayload({
              x,
              series,
              formatValue,
            }),
          buildPiePayload: (slice, pie) =>
            buildPiePayload(slice, pie, formatValue),
        },
      }
    },
  )

/** Resolve tooltip options from override, then content-node state. */
const setupInput = () =>
  RenderPipeline.updateInput((input, ctx) =>
    pipe(
      input,
      Options.fallback(
        chartTooltipOptions,
        ChartDataParts.getChartState()(ctx),
      ),
      Options.applyDefaults(chartTooltipOptions),
      Options.validate(chartTooltipOptions),
    ),
  )

/** Same resolution for `_tooltip`. */
const setup_tooltipInput = () =>
  RenderPipeline.updateInput((input, ctx) => ({
    ...input,
    _tooltip: pipe(
      input._tooltip ?? {},
      Options.fallback(
        chartTooltipOptions,
        ChartDataParts.getNestedState('_tooltip')(ctx),
      ),
      Options.applyDefaults(chartTooltipOptions),
    ),
  }))

export const ChartTooltipInputParts = {
  setupInput,
  setup_tooltipInput,
}

export const ChartTooltipOutputParts = {
  setTooltip,
}

export const ChartTooltipUtils = {
  clampTooltipPosition,
  formatValue,
  formatCategoryLabel,
  resolveSeriesLabel,
  buildPayload,
  buildPiePayload,
  categoryGuidePosition,
  SeriesMarker,
  TooltipKvRow,
}
